import {
  ProFormCheckbox,
  ProFormInstance,
  ProFormRadio,
  ProFormRate,
  ProFormText,
  ProFormTextArea,
  StepsForm,
} from '@ant-design/pro-components';
import { Card, Empty, Image, Spin, message } from 'antd';
import { QuestionType, Session, SessionMetadata } from 'pages/khaosat/models';
import React, { useEffect, useRef, useState } from 'react';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { observer } from 'mobx-react';
import { surveyPresenter, surveyStore } from 'stores/root_store';
import { cloneDeep } from 'lodash';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  getFormCompleted,
  setFormCompleted,
} from 'utils/localStorageHelper';
import noData from 'assets/images/no-comment.png';
import { getIdFromNameId, makeCompleteKey } from 'utils/formatHelper';
import useWindowSize from 'hooks/useWindowSize';
import './style.css';

type TypeRender =
  | {
    metaData: SessionMetadata;
    questions: Session[];
  }
  | undefined;

const createFeedback = () => {
  return observer(() => {
    const { nameId } = useParams();
    const [searchParams] = useSearchParams();
    const taskId = searchParams.get('TaskId'); // dùng để phân biệt đã submit theo task

    const safeDecode = (s: any) => {
      const str = String(s ?? '');

      // Chỉ thử decode khi có dấu hiệu encoded
      // (giúp tránh decode text user nhập bình thường)
      if (!/[%(+)]/.test(str)) return str;

      try {
        // nếu có dấu + kiểu querystring => space
        return decodeURIComponent(str.replace(/\+/g, ' '));
      } catch {
        return str;
      }
    };

    // render các phần và câu hỏi
    const [dataRender, setDataRender] = useState<TypeRender>(undefined);
    // Phần hiện tại
    const [current, setCurrent] = useState(0);
    // loading
    const [loading, setLoading] = useState(nameId ? true : false);

    const desc = ['Rất kém', 'Kém', 'Bình thường', 'Hài lòng', 'Rất hài lòng'];

    // chuyển phần
    const [nextSession, setNextSession] = useState({
      isSwitch: false,
      next: undefined as number | undefined,
      prev: undefined as number | undefined,
    });

    // ✅ isComplete theo surveyId + taskId
    const [isComplete, setIsComplete] = useState(() => {
      const listComleted = getFormCompleted();
      const surveyId = getIdFromNameId(nameId + '');
      const key = makeCompleteKey(surveyId, taskId);
      return listComleted.includes(key);
    });

    // ✅ khi đổi taskId (cùng page), cần sync lại isComplete
    useEffect(() => {
      const listComleted = getFormCompleted();
      const surveyId = getIdFromNameId(nameId + '');
      const key = makeCompleteKey(surveyId, taskId);
      setIsComplete(listComleted.includes(key));
    }, [nameId, taskId]);

    const formMapRef = useRef<
      React.MutableRefObject<ProFormInstance<any> | undefined>[]
    >([]);

    const getDetailSurvey = async () => {
      if (nameId) {
        const idSurvey = getIdFromNameId(nameId);
        await surveyPresenter.getSurveyById(surveyStore, {
          id: idSurvey,
          taskId: taskId || undefined,
        });

        if (surveyStore.surveyDetail) {
          const tempData = cloneDeep(surveyStore.surveyDetail);
          const lastSession = tempData.sections.pop();

          const t:
            | {
              metaData: SessionMetadata;
              questions: Session[];
            }
            | undefined = {
            metaData: {
              description: tempData.description,
              title: tempData.name,
              image: tempData.imageUrl,
              descriptionLast: lastSession?.sectionName,
              customerName: tempData.customerName,
              status: tempData.actived,

              // 2 field
              aliasName: tempData.aliasName,
              validDate: tempData.validDate,
              id: tempData.id,
            },
            questions: tempData.sections.map((s) => ({
              id: s.sectionId,
              description: s.description,
              title: s.sectionName,
              questions: s.questions?.map((q) => ({
                id: q.questionId,
                title: q.questionName,
                hasPoint: q.isScore,
                required: q.isRequired,
                type: q.type as QuestionType,
                answers: q.answers?.map((ans) => ({
                  title: ans.answer,
                  score: ans.score,
                  part: ans.sectionId,
                })),
              })),
            })),
          };

          setDataRender(t);
        }

        setTimeout(() => {
          setLoading(false);
        }, 1500);
      }
    };

    useEffect(() => {
      getDetailSurvey();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const windowSize = useWindowSize();

    return loading ? (
      <div className=" fixed z-[100] inset-0 flex justify-center items-center h-screen w-screen bg-white">
        <Spin rootClassName=""></Spin>
      </div>
    ) : dataRender ? (
      <div className="w-full md:py-3 md:px-5">
        <div className="max-w-[700px] mx-auto">
          {/* header */}
          <div className="text-center md:text-left flex flex-col items-center justify-center bg-white pb-3 mb-8 rounded-sm shadow-box">
            <div className="w-full h-3 bg-primary rounded-t-sm"></div>

            <div className="my-2">
              <Image
                preview={false}
                className="rounded-[10px] w-[350px] md:!max-w-[600px]"
                src={dataRender.metaData.image}
              />
            </div>

            <h1 className="uppercase whitespace-normal text-center text-xl px-2 font-bold mt-4 text">
              {dataRender.metaData.title}
            </h1>

            <div className="max-w-[600px] px-3 md:px-1 md:py-2">
              <p className="whitespace-normal text-center text-base">
                {dataRender.metaData.description}
              </p>
            </div>

            <div className='text-center px-2 py-1' style={{ display: dataRender.metaData.customerName ? 'block' : 'none' }}>
              <span className='text-sm'>Nhà cung cấp: {dataRender.metaData.customerName}</span>
            </div>
          </div>

          {/* TEST FORM */}
          <div className="mt-4 [&_.ant-pro-steps-form-step]:!mt-1 border-tab-step">
            {/* Nếu hoàn thành đánh giá thì ẩn form (✅ theo survey + task) */}
            {!isComplete && (
              <StepsForm
                stepsRender={(steps, dom) => {
                  if ((dataRender?.questions.length || 0) < 2) return <></>;
                  return dom;
                }}
                stepsProps={{
                  style: {
                    width: '100%',
                    margin: windowSize[0] < 500 ? '0 40px' : '0',
                  },
                }}
                current={current}
                onCurrentChange={(curr) => {
                  if (nextSession.isSwitch) {
                    if (curr > current) setCurrent(nextSession.next || 0);
                    else setCurrent(nextSession.prev || 0);
                  } else {
                    setCurrent(curr);
                  }
                }}
                onFormChange={(name, info) => {
                  const value = info.changedFields.length
                    ? info.changedFields[0].value + ''
                    : undefined;

                  if (value && value.includes('_')) {
                    const partId = value.split('_')[0];
                    const partIndex = dataRender?.questions.findIndex(
                      (q) => q.id === partId,
                    );
                    setNextSession({
                      isSwitch: true,
                      next: partIndex,
                      prev: current,
                    });
                  } else {
                    setNextSession({
                      isSwitch: false,
                      next: undefined,
                      prev: undefined,
                    });
                  }
                }}
                containerStyle={{
                  background: 'white',
                  padding: '10px',
                  borderRadius: '3px',
                  marginTop: '15px',
                  width: '100%',
                  minWidth: 'auto',
                }}
                formMapRef={formMapRef}
                onFinish={async (values) => {
                  const transformedObject = {
                    answers: [] as any[],
                    surveyId: nameId ? getIdFromNameId(nameId) : '',
                    taskId, // ✅ giữ nguyên
                  };

                  for (const sectionKey in values) {
                    const section = values[sectionKey];
                    for (const questionId in section) {
                      const answerData = section[questionId];
                      for (const key in answerData) {
                        const answer = answerData[key];

                        let answerString = '';
                        let score = undefined as number | undefined;

                        if (Array.isArray(answer)) {
                          const listAnswer = answer.map((a: string) => {
                            const check = a.split('*sc');
                            score =
                              check.length > 1
                                ? (score || 0) + Number(check[1])
                                : score;
                            return check.length > 1
                              ? safeDecode(check[0])
                              : safeDecode(a);
                          });
                          answerString = listAnswer.join(', ');
                        } else {
                          const arrString1 = String(answer).split('_');

                          if (arrString1.length > 1) {
                            const arrString2 = arrString1[1].split('*sc');
                            answerString = safeDecode(arrString2[0]);
                            score =
                              arrString2.length > 1
                                ? Number(arrString2[1])
                                : undefined;
                          } else {
                            const arrString2 = String(answer).split('*sc');
                            answerString = safeDecode(arrString2[0]);
                            score =
                              arrString2.length > 1
                                ? Number(arrString2[1])
                                : undefined;
                          }
                        }

                        transformedObject.answers.push({
                          questionId,
                          answer: answerString,
                          score: score,
                        });
                      }
                    }
                  }

                  // Nếu không trả lời câu hỏi vẫn phải truyền `null`
                  const data = cloneDeep(dataRender);
                  const temp = data.questions.flatMap((f) =>
                    f.questions?.map((m) => m),
                  );

                  temp.forEach((f) => {
                    if (
                      transformedObject.answers.findIndex(
                        (ans) => ans.questionId === f?.id,
                      ) === -1
                    ) {
                      transformedObject.answers.push({
                        questionId: f?.id,
                        answer: null,
                      });
                    }
                  });

                  const result = await surveyPresenter.saveDataAnswer(
                    transformedObject,
                  );

                  if (result.isSuccess) {
                    // ✅ đánh dấu complete theo surveyId + taskId
                    setIsComplete(true);
                    const listComleted = getFormCompleted();
                    const surveyId = getIdFromNameId(nameId + '');
                    const key = makeCompleteKey(surveyId, taskId);

                    if (!listComleted.includes(key)) {
                      listComleted.push(key);
                      setFormCompleted(listComleted);
                    }
                  } else {
                    message.error(result.message);
                  }
                }}
              >
                {dataRender?.questions.map((item, index) => {
                  return (
                    <StepsForm.StepForm
                      className="!mt-0"
                      key={index}
                      name={`step${index}`}
                      title={`Phần ${index + 1}`}
                    >
                      <div className="bg-primary rounded-[2px] md:mb-2 text-white  md:py-2">
                        <div
                          className={`font-semibold whitespace-normal !py-1 text-center text-base ${!item.title ? 'hidden' : ''
                            }`}
                        >
                          {item.title}
                        </div>
                        <div
                          className={`font-semibold whitespace-normal text-center ${!item.description ? 'hidden' : ''
                            }`}
                        >
                          {item.description}
                        </div>
                      </div>

                      {item.questions?.map((ques, idx) => {
                        return (
                          <div key={idx}>
                            <Card className="[&_.ant-card-body]:!py-0 !border-none">
                              <p className="font-semibold mb-2">
                                {ques.title}{' '}
                                {ques.required ? (
                                  <span className="font-normal text-red-500">
                                    {' '}
                                    *
                                  </span>
                                ) : null}
                              </p>

                              {/* câu hỏi nhiều lựa chọn */}
                              {ques.type === QuestionType.MULTI_CHOICE ? (
                                <ProFormCheckbox.Group
                                  layout="vertical"
                                  name={[`phan${index + 1}`, `${ques.id}`, 'multiChoice']}
                                  rules={[
                                    {
                                      required: ques.required,
                                      message: (
                                        <div className="flex items-center gap-x-1 leading-none">
                                          <AiOutlineInfoCircle className="text-base" />
                                          <span>Đây là một câu hỏi bắt buộc</span>
                                        </div>
                                      ),
                                    },
                                  ]}
                                  options={ques.answers?.map((item) => item.title)}
                                />
                              ) : null}

                              {/* câu hỏi trắc nghiệm */}
                              {ques.type === QuestionType.QUIZ ? (
                                <ProFormRadio.Group
                                  layout="vertical"
                                  rules={[
                                    {
                                      required: ques.required,
                                      message: (
                                        <div className="flex items-center gap-x-1 leading-none">
                                          <AiOutlineInfoCircle className="text-base" />
                                          <span>Đây là một câu hỏi bắt buộc</span>
                                        </div>
                                      ),
                                    },
                                  ]}
                                  name={[`phan${index + 1}`, `${ques.id}`, 'quiz']}
                                  options={ques.answers?.map((item) => {
                                    const valueEn = encodeURIComponent(item.title);
                                    return {
                                      value: `${item.part ? item.part + '_' : ''}${valueEn}${item.score ? '*sc' + item.score : ''
                                        }`,
                                      label: item.title,
                                    };
                                  })}
                                />
                              ) : null}

                              {/* câu hỏi trả lời ngắn */}
                              {ques.type === QuestionType.SHORT_ANSWER ? (
                                <ProFormText
                                  placeholder="Vui lòng nhập câu trả lời"
                                  rules={[
                                    {
                                      required: ques.required,
                                      message: (
                                        <div className="flex items-center gap-x-1 leading-none">
                                          <AiOutlineInfoCircle className="text-base" />
                                          <span>Đây là một câu hỏi bắt buộc</span>
                                        </div>
                                      ),
                                    },
                                  ]}
                                  name={[`phan${index + 1}`, `${ques.id}`, 'shortText']}
                                />
                              ) : null}

                              {/* câu hỏi trả lời đoạn */}
                              {ques.type === QuestionType.LONG_ANSWER ? (
                                <ProFormTextArea
                                  name={[`phan${index + 1}`, `${ques.id}`, 'largeText']}
                                  placeholder="Vui lòng nhập câu trả lời"
                                  rules={[
                                    {
                                      required: ques.required,
                                      message: (
                                        <div className="flex items-center gap-x-1 leading-none">
                                          <AiOutlineInfoCircle className="text-base" />
                                          <span>Đây là một câu hỏi bắt buộc</span>
                                        </div>
                                      ),
                                    },
                                  ]}
                                />
                              ) : null}

                              {/* câu hỏi khảo sát đánh giá sao */}
                              {ques.type === QuestionType.RATE ? (
                                <div className="flex justify-center items-center flex-col">
                                  <ProFormRate
                                    fieldProps={{
                                      allowHalf: false,
                                      style: {
                                        fontSize: '35px',
                                      },
                                      tooltips: desc,
                                    }}
                                    rules={[
                                      {
                                        required: ques.required,
                                        message: (
                                          <div className="flex items-center gap-x-1 leading-none">
                                            <AiOutlineInfoCircle className="text-base" />
                                            <span>Đây là một câu hỏi bắt buộc</span>
                                          </div>
                                        ),
                                      },
                                    ]}
                                    name={[`phan${index + 1}`, `${ques.id}`, 'rate']}
                                  />

                                  {/* Text hiển thị khi onchange Rate */}
                                  <div
                                    onClick={() => {
                                      console.log(
                                        formMapRef.current[index].current?.getFieldValue([
                                          `phan${index + 1}`,
                                          `${ques.id}`,
                                          'rate',
                                        ]),
                                      );
                                    }}
                                    className="-mt-5"
                                  >
                                    {desc[
                                      (formMapRef.current[index]?.current?.getFieldValue([
                                        `phan${index + 1}`,
                                        `${ques.id}`,
                                        'rate',
                                      ]) || 0) - 1
                                    ]}
                                  </div>
                                </div>
                              ) : null}
                            </Card>
                          </div>
                        );
                      })}
                    </StepsForm.StepForm>
                  );
                })}
              </StepsForm>
            )}

            {isComplete && (
              <div className="bg-white py-8 px-3 rounded-sm shadow-box">
                <p className="text-center whitespace-pre-line text-base font-semibold">
                  {dataRender?.metaData.descriptionLast}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    ) : (
      <div className="h-screen w-screen flex justify-center items-center">
        <Empty
          image={noData}
          imageStyle={{ height: 150, display: 'flex', justifyContent: 'center' }}
          description={
            <h3 className="text-base font-bold">Mẫu khảo sát không tồn tại !</h3>
          }
        />
      </div>
    );
  });
};

export default createFeedback;
